import React, { ReactNode, useRef, useState } from 'react';
import { ButtonV3, ButtonV3Props } from '../ButtonV3/ButtonV3';
import { ButtonColorway } from '../ButtonV3/types';
import { ConfettiV2, ConfettiV2Handle, ConfettiV2Props } from '../ConfettiV2/ConfettiV2';
import styles from './animatedButton.module.less';

export type AnimatedButtonProps = ButtonV3Props &
  Pick<ConfettiV2Props, 'confettiColors' | 'confettiSpreadHeight' | 'confettiSpreadWidth'> & {
    /** Changes button color on click; defaults to true */
    showColorAnimation?: boolean;
    /** Shows confetti animation around button on click; defaults to true */
    showConfettiAnimation?: boolean;
    /** Colorway to change button to on click if showColorAnimation is true; defaults to sky */
    successColorway?: ButtonColorway;
    /** Content to display on click */
    successContent?: ReactNode;
  };

/**
 * Button for which you can trigger the following animations on click:
 *   - Confetti animation from around the button
 *   - Button color change
 */
export const AnimatedButton = ({
  children,
  confettiColors,
  confettiSpreadHeight,
  confettiSpreadWidth,
  onClick,
  showColorAnimation = true,
  showConfettiAnimation = true,
  successColorway = 'sky',
  successContent,
  ...props
}: AnimatedButtonProps): JSX.Element => {
  const animatedButtonContainerRef = useRef<HTMLDivElement | null>(null);
  const animatedButtonRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);
  const confettiRef = useRef<ConfettiV2Handle | null>(null);

  const [triggerColorAnimation, setTriggerColorAnimation] = useState(false);
  const [animationAlreadyTriggered, setAnimationAlreadyTriggered] = useState(false);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (showColorAnimation && !animationAlreadyTriggered) {
      setTriggerColorAnimation(true);
    }

    if (showConfettiAnimation && !animationAlreadyTriggered) {
      confettiRef.current?.triggerConfettiAnimation();
    }

    if (!animationAlreadyTriggered) {
      setAnimationAlreadyTriggered(true);
      e.preventDefault();
      await new Promise(() => {
        setTimeout(() => {
          // Programmatically trigger the button's default action after animation delay
          animatedButtonRef.current?.click();
          onClick?.(e);
        }, 2000);
      });
    }
  };

  return (
    <div className={styles.animatedButtonContainer} ref={animatedButtonContainerRef}>
      <ButtonV3
        className={styles.animatedButton}
        colorway={animationAlreadyTriggered && triggerColorAnimation ? successColorway : undefined}
        innerRef={animatedButtonRef}
        onClick={handleClick}
        {...props}
      >
        {animationAlreadyTriggered && successContent ? successContent : children}
      </ButtonV3>
      <ConfettiV2
        confettiColors={confettiColors}
        confettiSpreadHeight={confettiSpreadHeight}
        confettiSpreadWidth={confettiSpreadWidth}
        containerRef={animatedButtonContainerRef}
        ref={confettiRef}
        targetRef={animatedButtonRef}
      />
    </div>
  );
};
